import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { api } from "../../lib/api";
import { toast } from "sonner";

const MAX_UPLOAD_MB = 10;
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];

async function uploadFile(file) {
  if (!ACCEPT.includes(file.type)) throw new Error(`Format non supporté : ${file.name}`);
  if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
    throw new Error(`${file.name} dépasse ${MAX_UPLOAD_MB} Mo`);
  }
  const { data } = await api.post("/admin/uploads/presign", {
    content_type: file.type,
    size: file.size,
  });
  await axios.put(data.upload_url, file, {
    headers: { "Content-Type": file.type },
    transformRequest: [(d) => d],
  });
  return data.public_url;
}

export default function AdminSettings() {
  const [s, setS] = useState({
    hero_image_url: "",
    hero_cta_label: "",
    hero_cta_link: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings")
      .then(r => setS(prev => ({ ...prev, ...r.data })))
      .finally(() => setLoading(false));
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setUploading(true);
      const url = await uploadFile(file);
      setS(prev => ({ ...prev, hero_image_url: url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(err.message || "Échec du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    try {
      setSaving(true);
      const { data } = await api.put("/admin/settings", s);
      setS(prev => ({ ...prev, ...data }));
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-berulia-inkmuted">Chargement…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl">
      <h1 className="font-heading text-3xl text-berulia-inkdeep mb-2">Paramètres du site</h1>
      <p className="text-berulia-inkmuted text-sm mb-8">Modifie la photo de couverture de la page d'accueil et le bouton d'appel à l'action.</p>

      <div className="space-y-8 bg-white border border-berulia-border p-6 rounded">
        {/* Hero image */}
        <div>
          <label className="block overline text-berulia-inkdeep mb-3">Photo de couverture (Hero)</label>
          {s.hero_image_url && (
            <div className="mb-3 border border-berulia-border rounded overflow-hidden">
              <img src={s.hero_image_url} alt="Aperçu hero" className="w-full h-56 object-cover" />
            </div>
          )}
          <div className="flex flex-wrap gap-3 items-center">
            <input ref={fileRef} type="file" accept={ACCEPT.join(",")} onChange={onFile} className="hidden" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-romance text-sm disabled:opacity-50">
              {uploading ? "Téléversement…" : "Téléverser une image"}
            </button>
            <span className="text-xs text-berulia-inkmuted">JPG / PNG / WebP — max {MAX_UPLOAD_MB} Mo</span>
          </div>
          <input
            type="text"
            value={s.hero_image_url}
            onChange={e => setS(p => ({ ...p, hero_image_url: e.target.value }))}
            placeholder="Ou colle une URL externe"
            className="mt-3 w-full border border-berulia-border px-3 py-2 text-sm"
          />
        </div>

        {/* CTA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block overline text-berulia-inkdeep mb-2">Libellé du bouton</label>
            <input
              type="text"
              value={s.hero_cta_label}
              onChange={e => setS(p => ({ ...p, hero_cta_label: e.target.value }))}
              className="w-full border border-berulia-border px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block overline text-berulia-inkdeep mb-2">Lien du bouton</label>
            <input
              type="text"
              value={s.hero_cta_link}
              onChange={e => setS(p => ({ ...p, hero_cta_link: e.target.value }))}
              placeholder="/boutique"
              className="w-full border border-berulia-border px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-berulia-border">
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="admin-settings-save"
            className="btn-romance disabled:opacity-50">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
